import { createFileRoute, notFound, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

import { ReviewerSignIn } from '../../modules/auth/components/reviewer-sign-in';
import { isReviewerSignInEnabled } from '../../modules/auth/reviewer-sign-in-enabled';
import { safeRedirectTo } from '../../modules/auth/safe-redirect-to';
import { setHtmlLoaderText, showHtmlLoader } from '../../ui/utils/html';

/**
 * Unlinked email + password sign-in for directory reviewers. Nothing in the app
 * links here; the URL is handed to a reviewer together with the demo account.
 *
 * The route answers 404 unless the build was made with
 * `VITE_REVIEWER_SIGNIN_ENABLED=true`. It stays on in production while the
 * connector is listed: directory reviewers keep testing with the same
 * credentials long after approval. Other environments leave it off.
 */
const AuthReviewerRoute = () => {
  const navigate = useNavigate();
  const { redirectTo } = useSearch({ from: '/auth/reviewer' });

  return (
    <ReviewerSignIn
      onSignedIn={() => {
        showHtmlLoader();
        setHtmlLoaderText('Loading account information ...');

        const target = safeRedirectTo(redirectTo);
        if (target) {
          navigate({ href: target });
          return;
        }
        navigate({ to: '/' });
      }}
    />
  );
};

const AuthReviewerRouteSearchSchema = z.object({
  redirectTo: z.string().optional(),
});

export const Route = createFileRoute('/auth/reviewer')({
  validateSearch: (params) => AuthReviewerRouteSearchSchema.parse(params),
  beforeLoad: () => {
    if (!isReviewerSignInEnabled()) {
      throw notFound();
    }
  },
  component: AuthReviewerRoute,
});
